import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useQuery } from "react-query";

import AdminHeader from "../header/admin-header";
import { UserTable } from "./user-table";
import { FilterBar } from "../filter/filter-bar";
import { ADMIN_PANEL_HEADER } from "@/constants/admin.constant";
import {
  FILTER_OPERATOR_BACKEND_MAP,
  USER_FILTER_CONFIG,
} from "@/constants/user-table.constants";

import { PaginationDefaults } from "@/constants/user.constants";
import { FilterCondition } from "@/interfaces/filter.interface";
import { PaginationInfo } from "@/interfaces/common.interface";

import useUserApiService from "@/services/user.service";

/**
 * UserManagerWrapper component is the main wrapper for the user management page.
 * Manages user data fetching, filtering, pagination, sorting, and search. Integrates
 * FilterBar and UserTable. Dynamically updates filter options based on available groups.
 * 
 * @component
 * @returns {JSX.Element} User manager page with filters, search, and user table
 * 
 * @example
 * ```tsx
 * <UserManagerWrapper />
 * ```
 */
export const UserManagerWrapper = () => {
  const { getUsers, getGroups } = useUserApiService();

  /** Pagination, sorting, search */
  const [page, setPage] = useState<number>(PaginationDefaults.PAGE);
  const [pageSize, setPageSize] = useState<number>(PaginationDefaults.PAGE_SIZE);
  const [sortKey, setSortKey] = useState("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  /** Filters */
  const [filterConditions, setFilterConditions] = useState<FilterCondition[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [pendingFilter, setPendingFilter] = useState<Partial<FilterCondition> | null>(null);
  const prevFilterOpenRef = useRef<boolean>(false);


  // fetch groups (for dynamic filter options)
  const { data: groupsResponse } = useQuery(
    ["groups"],
    () => getGroups(),
    {
      staleTime: Infinity,
      refetchOnMount: false,
    }
  );

  const groupOptions = useMemo(() => {
    return groupsResponse?.data?.map((g) => g.group_name) ?? [];
  }, [groupsResponse]);

  // Create dynamic USER_FILTER_CONFIG with real group options
  const dynamicFilterConfig = useMemo(() => {
    // Deep clone to avoid mutating the constant
    const cfg = structuredClone(USER_FILTER_CONFIG);

    const accessTierField = cfg.fields.find((f) => f.id === "accessTier");
    if (accessTierField) {
      accessTierField.options = groupOptions;
    }

    return cfg;
  }, [groupOptions]);


  useEffect(() => {
      if (prevFilterOpenRef.current && !isFilterOpen) {
          setFilterConditions([]);
          setPendingFilter(null);
          setPage(1);
      }
      prevFilterOpenRef.current = isFilterOpen;
  }, [isFilterOpen]);


  const convertConditionToBackendFormat = useCallback(
    (condition: FilterCondition | Partial<FilterCondition>, filters: Record<string, any>) => {
      if (!condition.field || !condition.value?.trim()) return;

      const fieldConfig = dynamicFilterConfig.fields.find(
        (f) => f.id === condition.field
      );
      if (!fieldConfig) return;

      const backendKey = fieldConfig.backendKey;

      if (condition.operator) {
        const operator = FILTER_OPERATOR_BACKEND_MAP[condition.operator];
        filters[backendKey] = {
          operator,
          value: condition.value,
        };
      }
    },
    [dynamicFilterConfig]
  );

  const backendFilters = useMemo(() => {
    const filters: Record<string, any> = {};

    filterConditions.forEach((c) => convertConditionToBackendFormat(c, filters));

    if (pendingFilter && pendingFilter.field && pendingFilter.value?.trim()) {
      const c: FilterCondition = {
        id: pendingFilter.id || `pending-${Date.now()}`,
        field: pendingFilter.field,
        operator: pendingFilter.operator!,
        value: pendingFilter.value,
      };
      convertConditionToBackendFormat(c, filters);
    }

    return Object.keys(filters).length > 0 ? filters : undefined;
  }, [filterConditions, pendingFilter]);

  const {
    data: userResponse,
    isLoading,
  } = useQuery(
    ["users", page, pageSize, sortKey, sortOrder, searchQuery, backendFilters],
    () =>
      getUsers({
        page,
        page_size: pageSize,
        sortBy: sortKey,
        sortOrder: sortOrder || undefined,
        search: searchQuery,
        filters: backendFilters,
      }),
    {
      refetchOnMount: true,
    }
  );

  const userData = Array.isArray(userResponse?.data) ? userResponse.data : [];
  const pagination: PaginationInfo | undefined = userResponse?.pagination;


  const handleSortChange = useCallback((key: string, order: "asc" | "desc" | null) => {
    setSortKey(key);
    setSortOrder(order);
    setPage(1);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handlePageSizeChange = useCallback((newSize: number) => {
    setPageSize(newSize);
    setPage(1);
  }, []);



  const handleConditionChange = useCallback((condition: FilterCondition) => {
    setFilterConditions((prev) => {
      // Check if this is a new condition (add row)
      const existingIndex = prev.findIndex((c) => c.id === condition.id);

      if (existingIndex >= 0) {
        // Update existing condition
        const updated = [...prev];
        updated[existingIndex] = condition;
        return updated;
      } else {
        // Add new condition
        return [...prev, condition];
      }
    });
    setPage(1); // Reset to first page when filters change
  }, []);

  const handleConditionRemove = useCallback((conditionId: string) => {
    setFilterConditions((prev) => {
      const afterRemoval = prev.filter((c) => c.id !== conditionId);
      return afterRemoval;
    });
    setPage(1); // Reset to first page when filters change
  }, []);

  const handleConditionAdd = useCallback(() => {
    // This is handled by handleConditionChange when add row is filled
    // Just reset the page
    setPage(1);
  }, []);

  return (
    <div className="h-full flex flex-col bg-background px-4 pb-4">

      {/* Header */}
      <div className="flex justify-between pb-6">
        <AdminHeader header={ADMIN_PANEL_HEADER.USER_MANAGER} />
      </div>

      {/* Filters */}
      <FilterBar
        isFilterOpen={isFilterOpen}
        onFilterToggle={() => setIsFilterOpen((prev) => !prev)}
        onFilterClose={() => setIsFilterOpen(false)}
        conditions={filterConditions}
        config={dynamicFilterConfig}
        onConditionChange={handleConditionChange}
        onConditionRemove={handleConditionRemove}
        onConditionAdd={handleConditionAdd}
        onPendingFilterChange={setPendingFilter}
        searchValue={searchQuery}
        onSearchChange={(v) => {
          setSearchQuery(v);
          setPage(1);
        }}
      />

      {/* Table Section */}
      <div className="mt-6 flex-1">
        <UserTable
          data={userData}
          pagination={pagination}
          sortKey={sortKey}
          sortOrder={sortOrder}
          onSortChange={handleSortChange}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};

